import { useCallback, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ShareModal from "./components/ShareModal";
import MovieCard from "@/components/feature/MovieCard";
import Toast from "@/components/base/Toast";
import { useFavorite } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";
import { formatDate, getMovieById, getRelatedMovies } from "@/utils/content";

export default function Detail() {
  const { id } = useParams();
  const movie = getMovieById(id);
  const { isFavorite, toggle } = useFavorite(id ?? "");
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState("");

  usePageMeta({
    title: movie
      ? `${movie.title} - 详细剧情解析与影视介绍`
      : "作品未找到 - 日本伦理影视作品",
    description: movie
      ? `${movie.title}（${movie.year}）${movie.genre}作品介绍：${movie.summary} 包含导演、主演、片长等资料，以及深度剧情解析与伦理元素解读。`
      : "该作品信息不存在或已下线，可返回影视作品列表继续浏览。",
    keywords: movie
      ? `${movie.title},${movie.genre},日本伦理影视,${movie.tags.join(",")},剧情解析`
      : "日本伦理影视作品",
  });

  const closeToast = useCallback(() => setToast(""), []);

  if (!movie) {
    return (
      <section className="flex min-h-[70vh] items-center justify-center px-4 pt-24">
        <div className="w-full max-w-lg rounded-lg border border-dashed border-background-700 bg-background-900 px-6 py-14 text-center">
          <i className="ri-error-warning-line text-3xl text-accent-500" aria-hidden="true"></i>
          <h1 className="mt-4 text-xl font-medium text-foreground-950">暂无详细信息</h1>
          <p className="mt-2 text-sm leading-relaxed text-foreground-600">
            该作品可能已从档案库中移除，或链接有误。你可以返回作品列表重新查找。
          </p>
          <Link
            to="/category"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm text-foreground-950 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
            返回作品列表
          </Link>
        </div>
      </section>
    );
  }

  const related = getRelatedMovies(movie, 3);

  const metaRows = [
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.cast.join(" / ") },
    { label: "类型", value: movie.genre },
    { label: "上映年份", value: `${movie.year} 年` },
    { label: "片长", value: `${movie.duration} 分钟` },
    { label: "制片国家", value: movie.country },
    { label: "资料更新", value: formatDate(movie.updatedAt) },
  ];

  return (
    <>
      <section className="relative overflow-hidden border-b border-background-800">
        <div className="absolute inset-0">
          <img
            src={movie.still ?? movie.poster}
            alt=""
            aria-hidden="true"
            className="h-full w-full scale-105 object-cover object-center opacity-40 blur-sm"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/90 to-background-950/70"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 to-transparent"></div>
        </div>

        <div className="relative mx-auto max-w-[1440px] px-4 pb-10 pt-24 md:px-12 md:pb-14 md:pt-32">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-accent-400">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <Link to="/category" className="transition-colors hover:text-accent-400">
              影视分类
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span className="truncate text-foreground-800">{movie.title}</span>
          </nav>

          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-8">
            <div className="md:col-span-3">
              <div className="w-40 overflow-hidden rounded-lg border border-background-700 bg-background-800 md:w-full">
                <img
                  src={movie.poster}
                  alt={`${movie.title}影视封面`}
                  title={`${movie.title} - ${movie.genre}作品介绍`}
                  className="aspect-[3/4] w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="md:col-span-9">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="rounded bg-accent-500 px-2.5 py-1 text-background-950">
                  {movie.genre}
                </span>
                <span className="rounded border border-background-600 px-2.5 py-1 text-foreground-600">
                  虚构作品示例
                </span>
              </div>
              <h1 className="mt-4 text-2xl font-semibold tracking-tight text-foreground-950 md:text-4xl">
                {movie.title}
              </h1>
              <p className="mt-2 font-label text-sm uppercase tracking-[0.18em] text-foreground-500">
                {movie.year} · {movie.duration} min · {movie.country}
              </p>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                {movie.summary}
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="font-label text-3xl leading-none text-accent-400">
                    {movie.rating.toFixed(1)}
                  </span>
                  <span className="text-xs text-foreground-500">
                    / 10
                    <br />
                    编辑评分
                  </span>
                </div>
                <span className="hidden h-8 w-px bg-background-700 sm:block"></span>
                <div className="flex flex-wrap gap-2">
                  {movie.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full border border-background-600 px-2.5 py-1 text-xs text-foreground-600"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    const next = toggle();
                    setToast(next ? "已加入收藏，保存在本机浏览器" : "已取消收藏");
                  }}
                  className={`flex items-center gap-2 rounded-md px-5 py-2.5 text-sm transition-colors ${
                    isFavorite
                      ? "bg-accent-500 text-background-950 hover:bg-accent-400"
                      : "border border-background-600 text-foreground-800 hover:border-accent-500 hover:text-accent-400"
                  }`}
                >
                  <i
                    className={isFavorite ? "ri-bookmark-fill" : "ri-bookmark-line"}
                    aria-hidden="true"
                  ></i>
                  <span className="whitespace-nowrap">{isFavorite ? "已收藏" : "收藏作品"}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="flex items-center gap-2 rounded-md border border-background-600 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-accent-500 hover:text-accent-400"
                >
                  <i className="ri-share-forward-line" aria-hidden="true"></i>
                  <span className="whitespace-nowrap">分享链接</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-4 py-12 md:px-12 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-8">
            <div>
              <h2 className="flex items-center gap-3 text-lg font-semibold text-foreground-950 md:text-xl">
                <span className="font-label text-sm text-accent-500">01</span>
                剧情简介
              </h2>
              <p className="mt-4 text-sm leading-7 text-foreground-600 md:text-base md:leading-8">
                {movie.plot}
              </p>
            </div>

            <div className="mt-12">
              <h2 className="flex items-center gap-3 text-lg font-semibold text-foreground-950 md:text-xl">
                <span className="font-label text-sm text-accent-500">02</span>
                深度剧情解析
              </h2>
              <p className="mt-3 text-xs text-foreground-500">
                由本站编辑撰写，包含剧情事实、人物动机与伦理处境的四个层次分析。
              </p>
              <div className="mt-6 space-y-6">
                {movie.analysis.map((paragraph, index) => (
                  <div key={paragraph.slice(0, 12)} className="flex gap-4">
                    <span className="mt-1 font-label text-xs tracking-[0.16em] text-accent-500">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <p className="flex-1 text-sm leading-7 text-foreground-600 md:text-base md:leading-8">
                      {paragraph}
                    </p>
                  </div>
                ))}
              </div>

              <blockquote className="mt-8 rounded-lg border-l-2 border-accent-500 bg-background-900 px-5 py-5">
                <p className="text-sm leading-relaxed text-foreground-700 md:text-base">
                  「理解并不等于原谅，但理解是继续共同生活的前提。」
                </p>
                <footer className="mt-3 text-xs text-foreground-500">
                  —— 本站编辑对《{movie.title}》的伦理议题归纳
                </footer>
              </blockquote>
            </div>

            <div className="mt-12">
              <div className="flex items-end justify-between border-b border-background-800 pb-4">
                <h2 className="flex items-center gap-3 text-lg font-semibold text-foreground-950 md:text-xl">
                  <span className="font-label text-sm text-accent-500">03</span>
                  相关推荐
                </h2>
                <Link
                  to="/category"
                  className="text-xs text-foreground-500 transition-colors hover:text-accent-400"
                >
                  浏览全部
                </Link>
              </div>
              <p className="mt-3 text-xs text-foreground-500">
                基于题材与标签相似度生成的前端演示推荐，非真实推荐算法。
              </p>
              <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3">
                {related.map((item) => (
                  <MovieCard key={item.id} movie={item} showSummary={false} />
                ))}
              </div>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <div className="rounded-lg border border-background-800 bg-background-900">
                <div className="border-b border-background-800 px-5 py-4">
                  <h2 className="text-sm font-medium text-foreground-950">作品资料</h2>
                </div>
                <dl className="divide-y divide-background-800">
                  {metaRows.map((row) => (
                    <div key={row.label} className="flex gap-4 px-5 py-3">
                      <dt className="w-20 shrink-0 text-xs text-foreground-500">{row.label}</dt>
                      <dd className="text-xs leading-relaxed text-foreground-800">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="mt-5 rounded-lg border border-background-800 bg-background-900 p-5">
                <div className="flex items-center gap-2 text-xs text-foreground-500">
                  <i className="ri-information-line text-accent-500" aria-hidden="true"></i>
                  内容说明
                </div>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  本页作品、导演、演员与制片信息均为虚构示例，用于演示影视资料与解析的呈现方式。站内不提供任何播放或下载链接。
                </p>
                <Link
                  to="/about"
                  className="mt-4 inline-flex items-center gap-1.5 text-xs text-accent-400 transition-colors hover:text-accent-300"
                >
                  查看编辑原则
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
              </div>

              <button
                type="button"
                onClick={() => {
                  const next = toggle();
                  setToast(next ? "已加入收藏，保存在本机浏览器" : "已取消收藏");
                }}
                className={`mt-5 flex w-full items-center justify-center gap-2 rounded-md px-4 py-3 text-sm transition-colors ${
                  isFavorite
                    ? "bg-accent-500 text-background-950 hover:bg-accent-400"
                    : "bg-primary-500 text-foreground-950 hover:bg-primary-600"
                }`}
              >
                <i
                  className={isFavorite ? "ri-bookmark-fill" : "ri-bookmark-line"}
                  aria-hidden="true"
                ></i>
                <span className="whitespace-nowrap">{isFavorite ? "已收藏本作" : "收藏本作"}</span>
              </button>
            </div>
          </aside>
        </div>
      </section>

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} title={movie.title} />
      <Toast message={toast} visible={Boolean(toast)} onClose={closeToast} />
    </>
  );
}